import {
    BLEND_MODES, Container, DisplayObject, Geometry, Graphics, IDestroyOptions, Matrix, Mesh, MeshMaterial, Renderer, RenderTexture,
    SCALE_MODES, Shader, Sprite, Texture, WRAP_MODES, Rectangle,
} from "pixi.js"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { TileMap, TileSize } from "game-common/models"
import { WaterEdge, waterSourceCell } from "game-common/room/water_edges"
import { GroundReader, RiseMapSource } from "./ground_rise"
import {
    BakeSteps, WATER_FACE_HEIGHT, WaterCells, WaterField, WaterRegion, bakeWaterFieldSteps, bankReflection, paintWaterBankSteps,
    waterBodies,
} from "./water_field"
import { RiverDefinition, riverBankHeight } from "game-common/room/river"
import { HillLevels, hillLevelsOf, hillHeightAt } from "game-common/room/hill_height"
import { WaterRockRipples, RockRippleContact } from "./water_rock_ripples"
import { riverFragment, riverPatternPixels } from "./river_shader"
import { riverFeatures } from "./river_features"
import { riverBandGeometry, riverSceneryDepth } from "./river_geometry"
import { HILL_BAND, hillBandZIndex, hillConfig } from "./hill_layer"
import { readWaterGround, waterGroundOverlayAt, waterGroundPatch } from "./water_ground"
import { setWaterPresence } from "./water_presence"

/**
 * WATER, DRAWN: the room's data-only `water` layer (common/room/ember/forest_pond.ts) as a body of water --
 * one Mesh per body with the fragment shader below, the bank baked under it (water_field.ts), and a
 * reflection texture the shader samples.
 *
 * Ground, so it sits with the ground: directly above the map1 tilemap and the raised-ground step, below
 * map2 (whose lily pads float on it) and every body. It never sorts against an entity.
 *
 * Per frame (update):
 *  - WADERS: a body whose feet stand in water is clipped at the waterline (a rectangle mask, which pixi
 *    draws as a scissor), so the water -- and anything floating on it in front of the legs -- shows where
 *    the legs were. Ankle deep at the shore, knee deep a step in, waist deep in the DEEPER core. The rings round the legs and spreading from them are drawn by the shader itself. Far
 *    cheaper than the old `submerged` crop, which rebuilt the body's art on every step in or out.
 *  - REFLECTIONS: a humanoid near or in the water has its current frame drawn upside down into the body's
 *    reflection texture, about its waterline (in the water) or the water's level below its feet (on the
 *    bank), over the baked reflection of the bank's own face.
 *
 * Every pattern is evaluated per WORLD pixel, so it stays crisp at the game's 2x and nothing swims.
 * Live tuning: window.water.enabled / .reflection / .waist, or window.water = false.
 */
export const WATER = { enabled: true, reflection: 0.55, faceHeight: WATER_FACE_HEIGHT, waist: 22 }

export const waterConfig = (): typeof WATER => {
    if (typeof window === "undefined") return WATER
    const w = window as any
    if (w.water === undefined) w.water = WATER
    const live = w.water
    if (live === false) return { ...WATER, enabled: false }
    return live && typeof live === "object" ? live : WATER
}

/** Cells of land baked round each body: the shore's blur and the bank's face both reach into it. */
const BODY_MARGIN_CELLS = 2
/**
 * Baking a frame may spend, in ms. A sea the size of the wreck beach's took one ~95 ms frame here and ~400 ms
 * on a budget machine; a slice at a time by the clock, no frame waits on it, however big the water.
 */
const BAKE_BUDGET_MS = 6
/** How far above the water a body still reflects in it (its feet on the bank). */
const REFLECT_REACH = 3 * TileSize
const MAX_WADERS = 8
/** The legs' ring, and how far it widens round a waist, in px. */
const RING_RADIUS = 8
const WAIST_RING_GROWTH = 3
/** How deep a wader goes in out of the deeper water (to the knee), in px; WATER.waist is how deep in it. */
const KNEE_SUBMERSION = 12

export interface WaterMapSource extends RiseMapSource {
    getRiver?(): RiverDefinition | undefined
    getHillLayer?(): TileMap | undefined
    getWaterHalo?(): { margin: number; rows: string[] } | undefined
    getWaterLayer?(): TileMap | undefined
    /** The borders a sea runs off (room/water_edges.ts): no bank is drawn along them. */
    getWaterOpenEdges?(): WaterEdge[]
}

/** A body standing somewhere this frame, in the world surface's coordinates. */
export interface WaterContact {
    id: string
    /** Where its feet meet the ground. */
    x: number
    y: number
    /** Projection from ground to screen at these feet, zero off hills. */
    lift?: number
    /** The frame it is showing now, for its reflection; absent for art with no frame of its own. */
    art?: DisplayObject
    /** Its renderable, which a wader is clipped on. */
    body?: Container
    /** Stationary scenery uses its own ripple budget and a shallower waterline. */
    stationary?: boolean
    radius?: number
    maxSink?: number
    /** Original scenery depth, before placement over a coalesced water strip. */
    depthOrder?: number
    flowX?: number
    flowY?: number
    submergeArt?: (localWaterline: number) => Texture | undefined
}

/** The rectangle keeping a wader's body above its waterline: a child of the renderable, so it moves with it. */
interface WaderClip { body: Container; clip: Graphics; line: number }

const VERT = `
attribute vec2 aVertexPosition;
attribute vec2 aUvs;
uniform mat3 translationMatrix;
uniform mat3 projectionMatrix;
varying vec2 vUvs;
void main() {
    vUvs = aUvs;
    gl_Position = vec4((projectionMatrix * translationMatrix * vec3(aVertexPosition, 1.0)).xy, 0.0, 1.0);
}`

const FRAG = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 vUvs;
uniform sampler2D uField;
uniform sampler2D uFlow;
uniform sampler2D uRefl;
uniform vec2 uSize;
uniform vec2 uOrigin;
uniform float uTime;
uniform float uFlowPx;
uniform float uReflAmt;
uniform float uDay;
uniform vec3 uR0; uniform vec3 uR1; uniform vec3 uR2; uniform vec3 uR3; uniform vec3 uR4; uniform vec3 uDeeper;
uniform vec3 uMid; uniform vec3 uLight; uniform vec3 uFoam; uniform vec3 uSpark; uniform vec3 uReflTint;
uniform vec4 uWaders[${MAX_WADERS}];
// The sea (a room whose water runs off an open edge): breakers instead of a pond's lapping rim.
uniform float uSea;
uniform vec2 uSeaDir;   // toward the shore, unit
uniform vec3 uFace;     // a breaker's shaded face
uniform vec3 uSwell;    // a far swell's face

float hash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
// Horizontal sky-glint dashes (the LPC water's own mark), and a finer shimmer drifting on its own.
float streaks(vec2 q, float t) {
    float a = vnoise(vec2(q.x / 12.0, q.y / 1.8));
    float b = vnoise(vec2(q.x / 5.0 + 7.0, q.y / 1.4 + t * 0.35));
    return a * 0.68 + b * 0.32;
}
float ringDist(vec2 p, vec2 c, float r) {
    vec2 d = (p - c) / vec2(1.0, 0.42);
    float e = length(d);
    vec2 g = vec2(d.x, d.y / 0.42) / max(e, 0.001);
    return abs(e - r) / max(length(g), 0.001);
}
void main() {
    // Everything is evaluated at a WORLD pixel's centre; p is in world px, so neighbouring bodies agree.
    vec2 local = floor(vUvs * uSize);
    vec2 p = uOrigin + local + 0.5;
    vec2 uv = (local + 0.5) / uSize;
    vec4 F = texture2D(uField, uv);
    float inD = F.r * 255.0 / 4.0;
    if (inD < 0.5) { gl_FragColor = vec4(0.0); return; }
    float shade = F.g;
    vec4 FL = texture2D(uFlow, uv);
    vec2 flow = (FL.rg * 255.0 - 128.0) / 127.0;
    float speed = length(flow);
    float still = 1.0 - smoothstep(0.12, 0.4, speed);
    float t = uTime;

    float depth = clamp((inD - 1.0) / 20.0, 0.0, 1.0);
    float band = depth * 4.0 + (vnoise(p / 6.0 + vec2(t * 0.04, 0.0)) - 0.5) * 0.9;
    vec3 col = band < 0.55 ? uR0 : band < 1.5 ? uR1 : band < 2.5 ? uR2 : band < 3.4 ? uR3 : uR4;
    // The deeper water: a darker core whose edge drifts, dithered a pixel or two where the bottom drops away.
    float drop = FL.b + (vnoise(p / 7.0 + vec2(t * 0.03, 0.0)) - 0.5) * 0.3;
    if (drop > 0.55 || (drop > 0.45 && hash(local) > 0.5)) col = uDeeper;

    // Flow-map advection: two phases half a period apart, crossfaded with the variance kept.
    float period = 2.6;
    float ph0 = fract(t / period), ph1 = fract(t / period + 0.5);
    float w0 = 1.0 - abs(2.0 * ph0 - 1.0), w1 = 1.0 - w0;
    vec2 vel = flow * uFlowPx + vec2(1.2, 0.0) * still;
    vec2 q0 = p - vel * (ph0 * period), q1 = p - vel * (ph1 * period) + vec2(31.0, 17.0);
    float n = ((streaks(q0, t) - 0.5) * w0 + (streaks(q1, t) - 0.5) * w1) / sqrt(w0 * w0 + w1 * w1) + 0.5;
    float lift = speed * 0.05;
    if (shade < 0.5) {
        if (n > 0.86 - lift) col = uLight;
        else if (n > 0.76 - lift) col = uMid;
    } else if (n > 0.84 - lift) col = mix(col, uMid, 0.5);

    // The shore: widest at the foot of a north bank (its face is in view), all but gone under a south one.
    float facing = F.b;
    float reach = mix(0.25, 1.0, facing);
    float lap = 1.0 + 1.5 * (0.5 + 0.5 * sin(t * 1.7 + vnoise(p / 11.0) * 6.283));
    float drag = vnoise(vec2(q0.x / 5.0, q0.y / 5.0)) * w0 + vnoise(vec2(q1.x / 5.0, q1.y / 5.0)) * w1;
    float foamW = mix(0.8 + drag * 2.6, lap, still) * reach;
    float fleck = vnoise(p / 2.3 + vec2(t * 0.25, 0.0));
    if (inD < foamW) col = fleck > 0.62 && shade < 0.5 ? uFoam : uLight;
    else if (inD < foamW + 1.0 + facing) col = uMid;
    float r = fract(t / 3.4 + vnoise(p / 15.0) * 0.6);
    if (still > 0.5 && abs(inD - (2.5 + r * 6.0) * reach) < 0.55 && hash(local + floor(r * 9.0)) > 0.25 + r * 0.9) col = uMid;

    if (uSea > 0.5) {
        // THE SEA: its own ramp by distance from the shore (the field holds up to 63 px), with LPC's glints.
        float sb = inD / 13.0 + (vnoise(p / 9.0 + vec2(t * 0.05, 0.0)) - 0.5) * 0.8;
        col = sb < 0.45 ? uR0 : sb < 1.35 ? uR1 : sb < 2.5 ? uR2 : sb < 3.6 ? uR3 : uR4;
        if (n > 0.86) col = uLight;
        else if (n > 0.78 && inD > 20.0) col = uMid;
        vec2 across = vec2(uSeaDir.y, -uSeaDir.x);
        float coast = dot(p, across);
        float along = dot(p, uSeaDir);
        // Long swells bend and drift independently; ease them into the surf instead of cutting at 44 px.
        float sw = fract((along + vnoise(vec2(coast / 37.0, t * 0.035)) * 18.0 - t * 12.0) / 46.0);
        float swell = smoothstep(36.0, 55.0, inD);
        if (sw < 0.045) col = mix(col, uSwell, swell);
        else if (sw < 0.075) col = mix(col, uMid, swell);

        // Each breaker lives past its arrival: the crest collapses, then its foam drains back out.
        // All contributions reach zero BEFORE fract resets, including the wave-keyed shape/strength.
        // A shared coastal delay makes neighbouring sections arrive unevenly without tearing the crest.
        float delay = (vnoise(vec2(coast / 85.0, 5.7)) - 0.5) * 0.16;
        float wash = 0.0;
        float white = 0.0;
        float froth = 0.0;
        float face = 0.0;
        float lace = vnoise(vec2(coast / 3.1 + sin(t * 0.37) * 0.5, (inD - t * 1.6) / 2.6));
        for (int k = 0; k < 3; k++) {
            float cyc = t / 6.8 + float(k) / 3.0 + delay;
            float ph = fract(cyc);
            float wave = floor(cyc) * 7.3 + float(k) * 19.1;
            float strength = mix(0.72, 1.0, hash(vec2(wave, 2.1)));
            float life = smoothstep(0.0, 0.12, ph) * (1.0 - smoothstep(0.80, 1.0, ph));
            // Decelerate in the shallows, then carry the crest through the waterline, never teleport it.
            float travel = min(ph / 0.76, 1.0);
            float c = 55.0 - 61.0 * (travel * (1.6 - 0.6 * travel));
            float bend = (vnoise(vec2(coast / 23.0 + t * 0.025, wave)) - 0.5) * 7.0;
            float rel = inD - c - bend * smoothstep(0.0, 18.0, c);
            float breaking = smoothstep(0.08, 0.30, ph);
            float collapse = smoothstep(-4.0, 9.0, c);
            float crest = (1.0 - smoothstep(0.8, 2.4, abs(rel - 0.7))) * breaking * life * strength;
            face = max(face, (1.0 - smoothstep(0.7, 3.5, abs(rel + 1.8))) * breaking * life * collapse * strength);
            white = max(white, crest);

            // Stretch and fragment the foam behind the crest; advect it slowly seaward during backwash.
            float trail = mix(5.0, 19.0, breaking) * strength;
            float behind = inD - max(c, 0.0);
            float tail = smoothstep(0.0, 2.0, behind) * (1.0 - smoothstep(2.0, trail, behind));
            float patches = vnoise(vec2(coast / 11.0 - t * 0.06, inD / 6.0 + wave));
            float foam = smoothstep(0.36, 0.73, lace * 0.65 + patches * 0.35);
            froth = max(froth, tail * foam * breaking * life * strength);

            // The same arrival drives a quick rush and slower retreat of the shallow wash.
            float surge = smoothstep(0.34, 0.58, ph) * (1.0 - smoothstep(0.62, 0.98, ph));
            wash = max(wash, surge * strength);
        }
        float film = 1.2 + 6.5 * wash;
        float wet = 1.0 - smoothstep(film - 1.0, film + 1.0, inD);
        col = mix(col, uR0, wet * (0.35 + 0.45 * wash));
        float washEdge = (1.0 - smoothstep(0.3, 1.2, abs(inD - film))) * wash;
        float washLace = smoothstep(0.28, 0.68, vnoise(vec2(coast / 2.8, inD / 2.0 - t * 0.5)));
        col = mix(col, uFace, face * 0.8);
        col = mix(col, uLight, froth * 0.55);
        col = mix(col, uFoam, max(white, max(froth * 0.85, washEdge * washLace)));
    }

    // Waders: a ring round the legs (its front shows where the clipped legs were), and rings spreading out.
    for (int i = 0; i < ${MAX_WADERS}; i++) {
        vec4 wd = uWaders[i];
        if (wd.w < 0.5) continue;
        float rd0 = ringDist(p, wd.xy, wd.z);
        if (rd0 < 0.75) col = p.y > wd.y ? uFoam : uLight;
        else if (rd0 < 1.6 && p.y > wd.y) col = uMid;
        for (int k = 0; k < 2; k++) {
            float rr = fract(t / 2.0 + float(k) * 0.5 + wd.w * 0.37);
            if (ringDist(p, wd.xy, wd.z + 3.0 + rr * 16.0) < 0.6 && hash(p + floor(t * 6.0)) > rr * 0.9) col = rr < 0.45 ? uLight : uMid;
        }
    }

    // Reflections: what stands over the water, wobbling a pixel row by row.
    float wob = floor(sin(p.y * 0.85 + t * 2.3) * 1.25 + 0.5);
    vec4 R = texture2D(uRefl, (local + 0.5 + vec2(wob, 0.0)) / uSize);
    if (R.a > 0.01) col = mix(col, (R.rgb / R.a) * uReflTint, uReflAmt * R.a);

    if (shade > 0.5) col *= 0.80;

    // Sun sparkles, by day: a pixel, at the peak a short dash.
    vec2 sc = floor(p / 5.0);
    float h = hash(sc * 1.7 + 3.1);
    if (uDay > 0.5 && h > 0.955 && shade < 0.5 && depth > 0.15) {
        vec2 sp = sc * 5.0 + floor(vec2(hash(sc + 1.3), hash(sc + 2.7)) * 5.0) + 0.5;
        float tw = sin(t * 2.6 + h * 90.0);
        vec2 dd = abs(p - sp);
        if (tw > 0.90 && dd.x + dd.y < 0.6) col = uSpark;
        if (tw > 0.97 && dd.y < 0.5 && dd.x < 1.6) col = uSpark;
    }
    gl_FragColor = vec4(col, 1.0);
}`

/**
 * The sea's colours: the LPC style guide's palette only (lpc-style-guide-palette) -- its water row (#156c99,
 * #0098b2, #00cfdf), pale aqua over sand, blue swell shadows, and white foam.
 */
const RIVER_PALETTE = {
    ramp: ["#a3bfa5", "#719f99", "#468c91", "#2d7284", "#245b73"],
    deeper: "#234b62", mid: "#5ca8b5", light: "#8cc6c8", foam: "#d9ead9", spark: "#f4f1dc", reflection: "#789c97",
    face: "#245b73", swell: "#468c91",
}

const SEA_PALETTE = {
    ramp: ["#a9c9ca", "#00cfdf", "#0098b2", "#156c99", "#156c99"],
    deeper: "#156c99",
    mid: "#0098b2", light: "#00cfdf", foam: "#ffffff", spark: "#ffffff", reflection: "#6f8fa0",
    face: "#156c99", swell: "#125f8c",
}

/** Which way a sea's open edges face, as the direction toward its shore. */
export const seaDirection = (open: readonly WaterEdge[]): [number, number] => {
    let x = 0, y = 0
    if (open.includes("north")) y += 1
    if (open.includes("south")) y -= 1
    if (open.includes("west")) x += 1
    if (open.includes("east")) x -= 1
    // Opposite open edges can cancel (especially where a river joins the sea).
    // A zero direction makes both shader coordinates constant, flashing every
    // deep-water pixel into the swell colour together instead of moving a wave.
    const len = Math.hypot(x, y)
    if (!len) return open.includes("north") ? [0, 1] : open.includes("south") ? [0, -1]
        : open.includes("west") ? [1, 0] : open.includes("east") ? [-1, 0] : [0, 1]
    return [x / len, y / len]
}

/** LPC's own blues -- #156c99 base, #0098b2 / #00cfdf marks -- with a deeper end and a lighter shallow. */
const PALETTE = {
    ramp: ["#2a86a8", "#1d78a2", "#156c99", "#125f8c", "#0f527c"],
    /** The waist-deep core: one step on down the ramp. */
    deeper: "#0c4570",
    mid: "#0d93b0", light: "#4fd0dd", foam: "#c5eeea", spark: "#f4fffd", reflection: "#6f8fa0",
}
const rgb = (hex: string): Float32Array =>
    new Float32Array([1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255))

interface Body {
    river?: RiverDefinition
    hills?: HillLevels
    hillMeshes?: Mesh<Shader>[]
    hillGroups?: Array<{ mesh: Mesh<Shader>; y0: number; y1: number; x0: number; x1: number; rowIndices: number; fine: Mesh<Shader>[] }>
    sea: boolean
    cells: WaterCells
    key: string
    region: WaterRegion
    /** What it was baked from; re-baked whenever that differs. */
    signature: string
    /** Waiting for a bake, which runs a slice at a time (see tick); the old art stays up until it is done. */
    queued: boolean
    pending?: {
        ground: Uint8ClampedArray
        fieldSteps?: BakeSteps<WaterField>
        field?: WaterField
        bankSteps?: BakeSteps<Uint8ClampedArray>
        bank?: Uint8ClampedArray
    }
    field?: WaterField
    textures: Texture[]
    reflection?: RenderTexture
    reflectionBase?: Sprite
    mesh?: Mesh<Shader>
    raster?: RenderTexture
    rasterMesh?: Mesh<Shader>
    rasterFrame?: number
    bank?: Sprite
    groundPatch?: Sprite
    uniforms?: any
    /** Reflections drawn last frame: when there are none now either, the texture is left as it is. */
    reflected: number
}

const regionGeometry = (x: number, y: number, w: number, h: number, region: WaterRegion): Geometry => {
    const u0 = (x - region.x0) / region.w, v0 = (y - region.y0) / region.h
    const u1 = (x + w - region.x0) / region.w, v1 = (y + h - region.y0) / region.h
    return new Geometry()
        .addAttribute("aVertexPosition", [x, y, x + w, y, x + w, y + h, x, y + h], 2)
        .addAttribute("aUvs", [u0, v0, u1, v0, u1, v1, u0, v1], 2)
        .addIndex([0, 1, 2, 0, 2, 3])
}

const canvasTexture = (rgba: Uint8ClampedArray, w: number, h: number): Texture | undefined => {
    if (typeof document === "undefined") return undefined
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const context = canvas.getContext("2d")
    if (!context) return undefined
    context.putImageData(new ImageData(rgba, w, h), 0, 0)
    return Texture.from(canvas, { scaleMode: SCALE_MODES.NEAREST })
}

export class WaterLayer extends Container {
    private source?: WaterMapSource
    private bodies = new Map<string, Body>()
    private queue: Body[] = []
    private startMs = typeof performance === "undefined" ? 0 : performance.now()
    private clips = new Map<string, WaderClip>()
    private eraser?: Graphics
    private riverPattern?: Texture
    private rockRipples = new WaterRockRipples()
    private rockCrops = new Map<string, { body: Sprite; original: Texture; cropped: Texture; line: number; owned: boolean }>()

    constructor(
        private readGround: GroundReader = readWaterGround,
        private now: () => number = () => (typeof performance === "undefined" ? 0 : performance.now()),
    ) {
        super()
        this.interactiveChildren = false
    }

    /** After every full map render: which bodies exist, and which changed. */
    sync = (source: WaterMapSource | undefined): void => {
        const config = waterConfig()
        const water = source?.getWaterLayer?.()
        if (!source || !water || !config.enabled) {
            this.source = source
            this.clearBodies()
            this.visible = false
            return
        }
        if (source.roomId !== this.source?.roomId) this.clearBodies()
        this.source = source
        this.visible = true
        const width = source.width(), height = source.height()
        const halo = source.getWaterHalo?.()
        const cells = waterCellsOf(water, width, height, source.getWaterOpenEdges?.(), halo)
        const ground = source.resolveTileMapFromLayerId("map1")
        const wanted = new Set<string>()
        for (const found of waterBodies(cells, width, height)) {
            // Waves belong to the connected body reaching an open sea border,
            // not every pond that happens to share its room with an ocean.
            const sea = (source.getWaterOpenEdges?.() ?? []).some(edge =>
                edge === "north" ? found.y0 === 0 : edge === "south" ? found.y1 === height - 1 :
                    edge === "west" ? found.x0 === 0 : found.x1 === width - 1)
            const membership = new Set(found.indices)
            const bodyCells: WaterCells = (x, y) => x < 0 || y < 0 || x >= width || y >= height
                ? sea || halo ? cells(x, y) : 0
                : membership.has(y * width + x) ? cells(x, y) : 0
            const cx0 = Math.max(0, found.x0 - BODY_MARGIN_CELLS), cy0 = Math.max(0, found.y0 - BODY_MARGIN_CELLS)
            const cx1 = Math.min(width - 1, found.x1 + BODY_MARGIN_CELLS), cy1 = Math.min(height - 1, found.y1 + BODY_MARGIN_CELLS)
            const key = `${cx0},${cy0},${cx1},${cy1}:${found.indices[0]}`
            wanted.add(key)
            const candidate = source.getRiver?.()
            const river = candidate?.points.some(p => membership.has(Math.floor(p.y) * width + Math.floor(p.x))) ? candidate : undefined
            const hills = river ? hillLevelsOf(source.getHillLayer?.()) : undefined
            const parts = [`${config.faceHeight}`, `${sea}`, JSON.stringify(river), JSON.stringify(source.getHillLayer?.())]
            for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) parts.push(`${cells(cx, cy)}:${ground?.[cx]?.[cy] ?? ""}:${waterGroundOverlayAt(source, cx, cy) ?? ""}`)
            const signature = parts.join(",")
            let body = this.bodies.get(key)
            if (!body) {
                body = { key, sea, cells: bodyCells, signature: "", queued: false, textures: [], reflected: 0,
                    region: { x0: cx0 * TileSize, y0: cy0 * TileSize, w: (cx1 - cx0 + 1) * TileSize, h: (cy1 - cy0 + 1) * TileSize } }
                this.bodies.set(key, body)
            }
            if (body.signature !== signature) {
                body.sea = sea
                body.river = river
                body.hills = hills
                body.cells = bodyCells
                body.signature = signature
                body.pending = undefined
                body.queued = true
            }
        }
        this.bodies.forEach((body, key) => { if (!wanted.has(key)) this.dropBody(body) })
        this.queue = Array.from(this.bodies.values()).filter(body => body.queued)
        this.publishPresence()
    }

    /** Bake some of whatever is waiting (see bakeSome), and move the time on. */
    tick = (): void => {
        if (!this.source) return
        if (this.queue.length) this.bakeSome(this.source)
        const now = (typeof performance === "undefined" ? 0 : performance.now()) - this.startMs
        this.rockRipples.tick(now / 1000)
        // Wrapped, so a long session never runs the shader's time past what a mediump float can hold.
        this.bodies.forEach(body => { if (body.uniforms) body.uniforms.uTime = (now / 1000) % 600 })
    }

    /**
     * A frame's worth of baking: slices of the shore, depth and bank until BAKE_BUDGET_MS is spent, so a budget
     * machine never stalls on a whole sea at once. Reading the ground and putting up the finished art are not
     * sliced: each takes a frame of its own. An atlas still decoding leaves the ground for the next frame.
     */
    private bakeSome = (source: WaterMapSource): void => {
        const body = this.queue[0]
        const config = waterConfig()
        const { region } = body
        if (!body.pending) {
            const ground = this.readGround(source, region.x0, region.y0, region.w, region.h)
            if (ground) body.pending = { ground }
            return
        }
        const pending = body.pending
        if (pending.field && pending.bank) {
            this.queue.shift()
            body.queued = false
            this.bake(body, pending.ground, pending.field, pending.bank)
            body.pending = undefined
            this.publishPresence()
            return
        }
        const deadline = this.now() + BAKE_BUDGET_MS
        do {
            if (!pending.field) {
                pending.fieldSteps ??= bakeWaterFieldSteps(
                    body.cells,
                    region, config.faceHeight, body.river ? riverFeatures(body.river, body.hills) : undefined)
                const next = pending.fieldSteps.next()
                if (next.done) { pending.field = next.value; pending.fieldSteps = undefined }
            } else if (!pending.bank) {
                pending.bankSteps ??= paintWaterBankSteps(pending.ground, pending.field.mask, region, config.faceHeight,
                    body.river ? (x, y) => riverBankHeight(body.river!, x, y) : undefined)
                const next = pending.bankSteps.next()
                if (next.done) { pending.bank = next.value; pending.bankSteps = undefined }
            } else return
        } while (this.now() < deadline)
    }

    /** px of water over the ground at a world point, or undefined on dry land (or before the bake). */
    depthAt = (px: number, py: number): number | undefined => this.wadeAt(px, py)?.depth

    /**
     * How a body standing at a world point wades: `depth` px from the shore, `deeper` 0..1 into the
     * waist-deep core, and `sink`, how far up its body the waterline comes. Undefined on dry land.
     */
    wadeAt = (px: number, py: number, waist = waterConfig().waist): { depth: number; deeper: number; sink: number } | undefined => {
        for (const body of this.bodies.values()) {
            const { x0, y0, w, h } = body.region
            const x = Math.floor(px - x0), y = Math.floor(py - y0)
            if (!body.field || x < 0 || y < 0 || x >= w || y >= h) continue
            const i = y * w + x
            if (!body.field.mask[i]) continue
            const depth = body.field.depth[i], deeper = body.field.deeper[i]
            const knee = Math.max(2, Math.min(KNEE_SUBMERSION, depth * 0.55))
            return { depth, deeper, sink: Math.round(knee + (Math.max(knee, waist) - knee) * deeper) }
        }
        return undefined
    }

    /**
     * Once a frame, after the bodies are laid out: who wades, and what reflects. `surface` is the world
     * surface the bodies stand in; `daylight` 0..1 turns the sun's sparkles off after dark.
     */
    update = (contacts: WaterContact[], surface: Container, renderer: Renderer | undefined, daylight = 1): Set<string> => {
        contacts = contacts.map(contact => ({ ...contact, y: contact.y + (contact.lift ?? 0) }))
        const wading = new Set<string>()
        const rippleContacts: RockRippleContact[] = []
        const config = waterConfig()
        this.bodies.forEach(body => {
            if (!body.uniforms || !body.mesh) return
            this.sortRiverAtContacts(body, contacts.filter(c => !c.stationary))
            body.uniforms.uReflAmt = config.reflection
            body.uniforms.uDay = daylight
            const waders: Array<{ contact: WaterContact; line: number; ring: number }> = []
            const reflect: Array<{ art: DisplayObject; mirror: number; lift?: number; x?: number }> = []
            const { x0, y0, w, h } = body.region
            for (const contact of contacts) {
                if (contact.x < x0 || contact.x >= x0 + w || contact.y < y0 - REFLECT_REACH || contact.y >= y0 + h) continue
                const wade = this.wadeAt(contact.x, contact.y, config.waist)
                if (wade && (contact.stationary || waders.length < MAX_WADERS)) {
                    const line = Math.round(contact.y) - Math.min(wade.sink, contact.maxSink ?? Infinity)
                    if (contact.stationary) {
                        const z = riverSceneryDepth(body.hillGroups ?? [], contact.x, contact.y,
                            contact.y - line, contact.depthOrder ?? contact.body?.zIndex ?? 0)
                        if (contact.body) contact.body.zIndex = z
                        rippleContacts.push({ id: contact.id, x: contact.x, line, lift: contact.lift ?? 0,
                            radius: contact.radius ?? 10, z, flowX: contact.flowX ?? 0, flowY: contact.flowY ?? 1 })
                    } else waders.push({ contact, line, ring: RING_RADIUS + WAIST_RING_GROWTH * wade.deeper })
                    wading.add(contact.id)
                    this.clipAt(contact, line - (contact.lift ?? 0))
                    if (contact.art && !contact.stationary) reflect.push({ art: contact.art, mirror: line, lift: contact.lift, x: contact.x })
                } else if (!wade && contact.art && !contact.stationary && this.waterBelow(contact.x, contact.y)) {
                    reflect.push({ art: contact.art, mirror: Math.round(contact.y) + config.faceHeight, lift: contact.lift, x: contact.x })
                }
            }
            body.uniforms.uWaderCount = waders.length
            const list = body.uniforms.uWaders as Float32Array
            list.fill(0)
            waders.forEach(({ contact, line, ring }, i) => list.set([Math.round(contact.x), line + 0.5, ring, 1 + i], i * 4))
            if (renderer) {
                this.renderReflections(body, reflect, waders, surface, renderer)
                this.renderWaterRaster(body, renderer)
            }
        })
        this.rockRipples.sync(rippleContacts, surface)
        this.clips.forEach((_, id) => { if (!wading.has(id)) this.releaseClip(id) })
        this.rockCrops.forEach((_, id) => { if (!wading.has(id)) this.releaseRockCrop(id) })
        return wading
    }

    /** Water somewhere in the 64px under a point: close enough for what stands there to show in it. */
    private waterBelow = (x: number, y: number): boolean => {
        for (let k = 4; k <= 64; k += 6) if (this.depthAt(x, y + k) !== undefined) return true
        return false
    }

    /** Split only at body depth boundaries; two shared-buffer draws usually replace a coarse batch. */
    private sortRiverAtContacts = (body: Body, contacts: WaterContact[]): void => {
        for (const group of body.hillGroups ?? []) {
            const cuts = new Set<number>()
            if (group.y1 - group.y0 > HILL_BAND) for (const contact of contacts) {
                if (contact.x < group.x0 - 64 || contact.x > group.x1 + 64) continue
                const critical = contact.body ? contact.body.zIndex - 100 + BODY_GROUND_OFFSET + hillConfig().shadowAllowance : contact.y + 20
                const cut = Math.floor(critical / HILL_BAND) * HILL_BAND
                if (cut > group.y0 && cut < group.y1) cuts.add(cut)
            }
            const rows = [group.y0, ...[...cuts].sort((a, b) => a - b), group.y1]
            const split = cuts.size > 0
            group.mesh.visible = !split
            if (split && group.mesh.parent) for (let i = 0; i < rows.length - 1; i++) {
                let mesh = group.fine[i]
                if (!mesh) {
                    mesh = new Mesh(group.mesh.geometry, group.mesh.shader)
                    mesh.name = "river-body-band"
                    group.mesh.parent.addChild(mesh)
                    group.fine.push(mesh); body.hillMeshes!.push(mesh)
                }
                mesh.start = (rows[i] - group.y0) * group.rowIndices
                mesh.size = (rows[i + 1] - rows[i]) * group.rowIndices
                mesh.zIndex = hillBandZIndex(rows[i + 1] - HILL_BAND) + .02
            }
            group.fine.forEach((mesh, i) => mesh.visible = split && i < rows.length - 1)
        }
    }

    private clipAt = (contact: WaterContact, line: number): void => {
        const body = contact.body
        if (!body || body.destroyed) return
        if (contact.stationary && body instanceof Sprite && this.cropRockAt(contact, body, line)) return
        let entry = this.clips.get(contact.id)
        if (entry && (entry.body !== body || entry.clip.destroyed)) {
            this.releaseClip(contact.id)
            entry = undefined
        }
        if (!entry) {
            // Something else is clipping this body already (a stair's opening): leave it be.
            if (body.mask) return
            const clip = new Graphics()
            clip.name = "water-clip"
            body.addChild(clip)
            body.mask = clip
            entry = { body, clip, line: NaN }
            this.clips.set(contact.id, entry)
        }
        const local = (line - body.y) / (body.scale.y || 1)
        if (entry.line === local) return
        entry.clip.clear().beginFill(0xffffff).drawRect(-96, local - 256, 192, 256).endFill()
        entry.line = local
    }

    /** A static rock crops its atlas frame once, preserving ordinary sprite batching instead of using a mask. */
    private cropRockAt = (contact: WaterContact, body: Sprite, line: number): boolean => {
        let entry = this.rockCrops.get(contact.id)
        if (entry && (entry.body !== body || body.texture !== entry.cropped)) {
            this.releaseRockCrop(contact.id); entry = undefined
        }
        const original = entry?.original ?? body.texture
        if (original.rotate || original.trim) return false
        const local = Math.max(1, Math.min(original.height, Math.floor((line - body.y) / (body.scale.y || 1))))
        if (entry?.line === local) return true
        this.releaseClip(contact.id)
        const frame = original.frame
        const seated = contact.submergeArt?.(local)
        const cropped = seated ?? new Texture(original.baseTexture, new Rectangle(frame.x, frame.y, frame.width, local))
        body.texture = cropped
        if (entry?.owned) entry.cropped.destroy()
        this.rockCrops.set(contact.id, { body, original, cropped, line: local, owned: !seated })
        return true
    }

    private releaseRockCrop = (id: string): void => {
        const entry = this.rockCrops.get(id)
        if (!entry) return
        if (!entry.body.destroyed && entry.body.texture === entry.cropped) entry.body.texture = entry.original
        if (entry.owned) entry.cropped.destroy()
        this.rockCrops.delete(id)
    }

    private releaseClip = (id: string): void => {
        const entry = this.clips.get(id)
        if (!entry) return
        if (!entry.body.destroyed && entry.body.mask === entry.clip) entry.body.mask = null
        if (!entry.clip.destroyed) entry.clip.destroy()
        this.clips.delete(id)
    }

    private renderReflections = (body: Body, reflect: Array<{ art: DisplayObject; mirror: number; lift?: number; x?: number }>,
        waders: Array<{ contact: WaterContact; line: number }>, surface: Container, renderer: Renderer): void => {
        if (!body.reflection || !body.reflectionBase) return
        if (body.river) { this.renderRiverReflections(body, reflect, waders, surface, renderer); return }
        if (!reflect.length && !body.reflected) return
        const { x0, y0 } = body.region
        renderer.render(body.reflectionBase, { renderTexture: body.reflection, clear: true })
        const inverse = surface.worldTransform.clone().invert()
        for (const { art, mirror, lift } of reflect) {
            if (!art.worldVisible || art.destroyed) continue
            // world (the surface's space) -> flipped about the waterline -> the reflection texture's pixels
            const flip = new Matrix(1, 0, 0, -1, -x0, 2 * mirror - (lift ?? 0) - y0)
            renderer.render(art, { renderTexture: body.reflection, clear: false, skipUpdateTransform: true,
                transform: flip.append(inverse) })
        }
        // A wader's legs, flipped, land ABOVE its waterline: rub them out -- the water reflects only what is above it.
        if (waders.length) {
            const eraser = this.eraser ??= Object.assign(new Graphics(), { blendMode: BLEND_MODES.ERASE })
            eraser.clear().beginFill(0xffffff)
            waders.forEach(({ contact, line }) => eraser.drawRect(Math.round(contact.x) - x0 - 24, line - y0 - 64, 48, 64))
            eraser.endFill()
            renderer.render(eraser, { renderTexture: body.reflection, clear: false })
        }
        // The bank goes over the bodies again: it stands between them and the water.
        if (reflect.length) renderer.render(body.reflectionBase, { renderTexture: body.reflection, clear: false })
        body.reflected = reflect.length
    }

    /** River reflections only redraw the small patch near visible bodies, rather than the entire channel. */
    private renderRiverReflections = (body: Body, reflect: Array<{ art: DisplayObject; mirror: number; lift?: number; x?: number }>,
        waders: Array<{ contact: WaterContact; line: number }>, surface: Container, renderer: Renderer): void => {
        body.uniforms.uReflecting = reflect.length ? 1 : 0
        if (!reflect.length) { body.reflected = 0; return }
        const r = body.region
        const minX = Math.min(...reflect.map(p => (p.x ?? r.x0) - 80)), maxX = Math.max(...reflect.map(p => (p.x ?? r.x0) + 80))
        const minY = Math.min(...reflect.map(p => p.mirror - 32)), maxY = Math.max(...reflect.map(p => p.mirror + 160))
        const capacity = (span: number, limit: number) => Math.min(limit, 2 ** Math.ceil(Math.log2(Math.max(128, span))))
        const w = capacity(maxX - minX, r.w), h = capacity(maxY - minY, r.h)
        const x0 = Math.max(r.x0, Math.min(r.x0 + r.w - w, Math.floor(minX)))
        const y0 = Math.max(r.y0, Math.min(r.y0 + r.h - h, Math.floor(minY)))
        if (body.reflection!.width !== w || body.reflection!.height !== h) body.reflection!.resize(w, h)
        body.uniforms.uReflRect.set([x0 - r.x0, y0 - r.y0, w, h])
        const inverse = surface.worldTransform.clone().invert()
        let clear = true
        for (const { art, mirror, lift } of reflect) {
            if (!art.worldVisible || art.destroyed) continue
            const flip = new Matrix(1, 0, 0, -1, -x0, 2 * mirror - (lift ?? 0) - y0)
            renderer.render(art, { renderTexture: body.reflection, clear, skipUpdateTransform: true, transform: flip.append(inverse) })
            clear = false
        }
        if (clear) body.uniforms.uReflecting = 0
        if (!clear && waders.length) {
            const eraser = this.eraser ??= Object.assign(new Graphics(), { blendMode: BLEND_MODES.ERASE })
            eraser.clear().beginFill(0xffffff)
            waders.forEach(({ contact, line }) => eraser.drawRect(Math.round(contact.x) - x0 - 24, line - y0 - 64, 48, 64))
            eraser.endFill()
            renderer.render(eraser, { renderTexture: body.reflection, clear: false })
        }
        body.reflected = reflect.length
    }

    /** Shade once per world pixel at 60 Hz; screen-size hill meshes only sample this cached surface. */
    private renderWaterRaster = (body: Body, renderer: Renderer): void => {
        if (!body.raster || !body.rasterMesh) return
        const frame = Math.floor(this.now() * .06)
        if (body.rasterFrame === frame) return
        renderer.render(body.rasterMesh, { renderTexture: body.raster, clear: true })
        body.rasterFrame = frame
    }

    private bake = (body: Body, ground: Uint8ClampedArray, field: WaterField, bank: Uint8ClampedArray): void => {
        this.releaseArt(body)
        const config = waterConfig()
        const { region } = body
        body.field = field
        const patch = this.source && waterGroundPatch(this.source, ground, region.x0, region.y0, region.w, region.h)
        const patchTexture = patch && canvasTexture(patch, region.w, region.h)
        if (patchTexture) {
            body.textures.push(patchTexture)
            body.groundPatch = new Sprite(patchTexture)
            body.groundPatch.position.set(region.x0, region.y0)
            this.addChild(body.groundPatch)
        }
        const bankTexture = canvasTexture(bank, region.w, region.h)
        if (bankTexture) body.textures.push(bankTexture)
        const reflection = bankReflection(ground, bank, field.mask, region, config.faceHeight)
        const options = { width: region.w, height: region.h, scaleMode: SCALE_MODES.NEAREST }
        const fieldTexture = Texture.fromBuffer(field.field, region.w, region.h, options)
        const flowTexture = Texture.fromBuffer(field.flow, region.w, region.h, options)
        body.textures.push(fieldTexture, flowTexture)
        const coordsTexture = field.coords ? Texture.fromBuffer(field.coords, region.w, region.h, options) : Texture.EMPTY
        if (field.coords) body.textures.push(coordsTexture)
        body.reflection = RenderTexture.create({ width: body.river ? 128 : region.w, height: body.river ? 128 : region.h, scaleMode: SCALE_MODES.NEAREST })
        const reflectionTexture = canvasTexture(reflection, region.w, region.h)
        if (reflectionTexture) body.textures.push(reflectionTexture)
        body.reflectionBase = new Sprite(reflectionTexture ?? Texture.EMPTY)
        const open = this.source?.getWaterOpenEdges?.() ?? []
        const sea = body.sea
        const pal = body.river ? RIVER_PALETTE : sea ? SEA_PALETTE : { ...PALETTE, face: PALETTE.mid, swell: PALETTE.mid }
        if (body.river && !this.riverPattern) {
            this.riverPattern = Texture.fromBuffer(riverPatternPixels(), 256, 256, { scaleMode: SCALE_MODES.NEAREST })
            this.riverPattern.baseTexture.wrapMode = WRAP_MODES.REPEAT
        }
        body.uniforms = {
            uField: fieldTexture, uFlow: flowTexture, uRefl: body.reflection,
            uBankRefl: reflectionTexture ?? Texture.EMPTY, uReflecting: 0,
            uReflRect: new Float32Array([0, 0, 128, 128]),
            uCoords: coordsTexture, uPattern: this.riverPattern ?? Texture.EMPTY, uWaderCount: 0,
            uBank: bankTexture ?? Texture.EMPTY, uProjected: body.hills ? 1 : 0,
            uSize: new Float32Array([region.w, region.h]), uOrigin: new Float32Array([region.x0, region.y0]),
            uTime: 0, uFlowPx: body.river ? 32 : 16, uReflAmt: config.reflection, uDay: 1,
            uR0: rgb(pal.ramp[0]), uR1: rgb(pal.ramp[1]), uR2: rgb(pal.ramp[2]), uR3: rgb(pal.ramp[3]),
            uR4: rgb(pal.ramp[4]), uDeeper: rgb(pal.deeper), uMid: rgb(pal.mid), uLight: rgb(pal.light), uFoam: rgb(pal.foam),
            uSpark: rgb(pal.spark), uReflTint: rgb(pal.reflection), uWaders: new Float32Array(MAX_WADERS * 4),
            uSea: sea ? 1 : 0, uSeaDir: new Float32Array(seaDirection(open)), uFace: rgb(pal.face), uSwell: rgb(pal.swell),
        }
        let shader = Shader.from(VERT, body.river ? riverFragment(MAX_WADERS) : FRAG, body.uniforms)
        if (body.river) {
            body.raster = RenderTexture.create({ width: region.w, height: region.h, scaleMode: SCALE_MODES.NEAREST })
            const flatRegion = { x0: 0, y0: 0, w: region.w, h: region.h }
            body.rasterMesh = new Mesh(regionGeometry(0, 0, region.w, region.h, flatRegion), shader)
            shader = new MeshMaterial(body.raster)
        }
        if (body.hills && this.parent) {
            body.hillMeshes = []
            body.hillGroups = []
            // Coalesce downhill/flat strips. Fine sorting is only needed where rising terrain can
            // overlap itself; one mesh per 8px row otherwise costs hundreds of WebGL draw calls.
            for (let y = region.y0; y < region.y0 + region.h;) {
                let end = Math.min(y + HILL_BAND * 8, region.y0 + region.h)
                let left = region.w, right = -1
                for (let row = y - region.y0; row < end - region.y0; row++) for (let x = 0; x < region.w; x++) {
                    const i = row * region.w + x
                    if (!field.mask[i] && !bank[i * 4 + 3]) continue
                    left = Math.min(left, x); right = Math.max(right, x)
                }
                if (right < left) { y = end; continue }
                const x0 = region.x0 + Math.max(0, Math.floor((left - 4) / 4) * 4), x1 = region.x0 + Math.min(region.w, Math.ceil((right + 5) / 4) * 4)
                let folds = false
                for (let yy = y; yy <= end && !folds; yy += 8) for (let x = x0; x <= x1; x += 16) {
                    if (hillHeightAt(body.hills, x, yy + 4) - hillHeightAt(body.hills, x, yy) > 3.5) { folds = true; break }
                }
                if (folds) end = Math.min(y + HILL_BAND, region.y0 + region.h)
                const mesh = new Mesh(riverBandGeometry(region, y, end, body.hills, x0, x1), shader)
                mesh.name = "river-hill-band"
                mesh.zIndex = hillBandZIndex(end - HILL_BAND) + .02
                this.parent.addChild(mesh)
                body.hillMeshes.push(mesh)
                body.hillGroups.push({ mesh, y0: y, y1: end, x0, x1, rowIndices: Math.ceil((x1 - x0) / 4) * 6, fine: [] })
                y = end
            }
            body.mesh = body.hillMeshes[0]
        } else {
            const geometry = regionGeometry(region.x0, region.y0, region.w, region.h, region)
            if (body.river) geometry.addAttribute("aTextureCoord", geometry.getBuffer("aUvs"), 2)
            body.mesh = new Mesh(geometry, shader)
            body.mesh.name = "water"
            this.addChild(body.mesh)
            if (bankTexture) {
                body.bank = new Sprite(bankTexture)
                body.bank.position.set(region.x0, region.y0)
                this.addChild(body.bank)
            }
        }
        body.reflected = 1 // draw the bank's reflection on the first update
    }

    /** The baked shore where there is one; the cells while a body still waits for its bake. */
    private isWater = (px: number, py: number): boolean => {
        for (const body of this.bodies.values()) {
            const { x0, y0, w, h } = body.region
            const x = Math.floor(px - x0), y = Math.floor(py - y0)
            if (x < 0 || y < 0 || x >= w || y >= h) continue
            if (body.field) {
                if (body.field.mask[y * w + x] === 1) return true
            } else if (body.cells(Math.floor(px / TileSize), Math.floor(py / TileSize)) > 0) return true
        }
        return false
    }

    /** grassAt and the footsteps ask the baked shore, not the cells, whether a point is water. */
    private publishPresence = (): void => {
        const roomId = this.source?.roomId
        if (!roomId) return
        setWaterPresence(roomId, this.bodies.size ? this.isWater : undefined)
    }

    private releaseArt = (body: Body): void => {
        body.rasterMesh?.destroy()
        body.raster?.destroy(true)
        body.rasterMesh = undefined; body.raster = undefined; body.rasterFrame = undefined
        if (body.hillMeshes) body.hillMeshes.forEach(mesh => mesh.destroy())
        else body.mesh?.destroy()
        body.hillMeshes = undefined
        body.hillGroups = undefined
        body.bank?.destroy()
        body.groundPatch?.destroy()
        body.groundPatch = undefined
        body.reflectionBase?.destroy()
        body.reflection?.destroy(true)
        body.textures.forEach(texture => texture.destroy(true))
        body.mesh = body.bank = body.reflectionBase = body.reflection = body.uniforms = undefined
        body.textures = []
        body.field = undefined
    }

    private dropBody = (body: Body): void => {
        this.releaseArt(body)
        body.queued = false
        body.pending = undefined
        this.bodies.delete(body.key)
        this.queue = this.queue.filter(waiting => waiting !== body)
    }

    private clearBodies = (): void => {
        this.rockCrops.forEach((_, id) => this.releaseRockCrop(id))
        this.rockRipples.clear()
        this.bodies.forEach(body => this.releaseArt(body))
        this.bodies.clear()
        this.queue = []
        if (this.source?.roomId) setWaterPresence(this.source.roomId, undefined)
    }

    displayObjects = (): DisplayObject[] => [...Array.from(this.bodies.values()).flatMap(body => body.hillMeshes ?? []), ...this.rockRipples.displayObjects()]

    /** True while bodies are still waiting to bake: the room-reveal curtain waits for it (MapRenderingManager). */
    get baking(): boolean {
        return this.queue.length > 0
    }

    destroy(options?: IDestroyOptions | boolean): void {
        Array.from(this.clips.keys()).forEach(this.releaseClip)
        this.eraser?.destroy()
        this.riverPattern?.destroy(true)
        this.rockRipples.destroy()
        this.clearBodies()
        super.destroy(options)
    }
}

/**
 * The water layer as the field reads it. Past a closed border it is dry, so a pond gets its bank all round;
 * past an open one (a sea) it reads as the border's own cell, so the water runs off the room with no bank.
 */
export const waterCellsOf = (water: TileMap | undefined, width: number, height: number, open: readonly WaterEdge[] = [], halo?: { margin: number; rows: string[] }): WaterCells => (x, y) => {
    if (!water) return 0
    if (halo && (x < 0 || y < 0 || x >= width || y >= height)) {
        const sample = halo.rows[y + halo.margin]?.[x + halo.margin]
        if (sample !== undefined) return Number(sample) || 0
    }
    const at = waterSourceCell(x, y, width, height, open)
    if (!at) return 0
    const v = water[at[0]]?.[at[1]]
    return v && v !== "0" ? Number(v) || 1 : 0
}
